import { ElementRef, Injectable, signal } from '@angular/core';

export const TOUCH_MOVING = 'touch-moving';
export const TOUCH_HOVER = 'touch-hover';
export const TOUCH_ACTIVE = 'touch-active';


interface PanningState {

	element: HTMLDivElement,

	//
	// Events handlers (maintain references so they can be removed)
	//

	onPointerDown: (event: PointerEvent) => void,
	onPointerMove: (event: PointerEvent) => void,
	onPointerUp: (event: PointerEvent) => void,
	onPointerEnter: () => void,
	onPointerLeave: () => void,

	/**
	 * If not null, then currently panning
	 */
	panning: null | {
		/** The scroll-top position when the panning started */
		startScrollTop: number,
		/** The pixel offset from where the pointer was at start */
		offset: number,
	},
}


@Injectable({ providedIn: 'root' })
export class EmulateTouchPanningService {

	readonly isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

	private _map: { [index: string]: PanningState } = {};

	public readonly emulating = signal(false);

	/**
	 * Add touch emulation. If the browser supports Touch natively
	 * then this function will be ignored.
	 * 
	 * @param key - A unique identifier for the current Div element
	 * @param scrollDiv - A Div element that is configured with overflow support
	 */
	async add(
		key: string,
		scrollDivRef: ElementRef<HTMLDivElement>,
	) {

		// if (this.isTouchDevice) return;

		//
		// Only add once!!
		//
		if (this._map[key]) return;

		const el = scrollDivRef.nativeElement;
		const state: PanningState = this._map[key] = {
			element: el,
			onPointerDown: (event: PointerEvent) => { this.onPointerDown(state, event) },
			onPointerMove: (event: PointerEvent) => { this.onPointerMove(state, event) },
			onPointerUp: (event: PointerEvent) => { this.onPointerUp(state, event) },
			onPointerEnter: () => { document.body.classList.add(TOUCH_HOVER); },
			onPointerLeave: () => { document.body.classList.remove(TOUCH_HOVER); },
			panning: null,
		};

		el.addEventListener('pointerdown', state.onPointerDown);
		el.addEventListener('pointermove', state.onPointerMove, { passive: false });
		el.addEventListener('pointerup', state.onPointerUp);
		el.addEventListener('pointercancel', state.onPointerUp);
		el.addEventListener('pointerenter', state.onPointerEnter);
		el.addEventListener('pointerleave', state.onPointerLeave);

		//
		// Hide the pointer cursor because we have a circle.
		//
		el.style.cursor = 'none';

		this.emulating.set(true);

	}

	async remove(key: string) {

		const state = this._map[key];

		if (state) {

			//
			// Clean up all the event listeners
			//
			const el = state.element;
			el.removeEventListener('pointerdown', state.onPointerDown);
			el.removeEventListener('pointermove', state.onPointerMove);
			el.removeEventListener('pointerup', state.onPointerUp);
			el.removeEventListener('pointercancel', state.onPointerUp);
			el.removeEventListener('pointerenter', state.onPointerEnter);
			el.removeEventListener('pointerleave', state.onPointerLeave);

			el.style.cursor = '';

			delete this._map[key];

			//
			// We are no longer emulating if the map is empty
			//
			this.emulating.set(Object.keys(this._map).length > 0);
		}
	}


	private onPointerDown = (state: PanningState, event: PointerEvent) => {

		//
		// Change the touch cursor styling
		//
		document.body.classList.add(TOUCH_ACTIVE);

		//
		// A non-null panning value means they are dragging/panning.
		//
		state.panning = {
			startScrollTop: state.element.scrollTop,
			offset: event.pageY,
		};

		//
		// Capture events until release in case drag goes outside the div
		//
		//state.element.setPointerCapture(event.pointerId);
	};


	private touchMovingTimeout = 0;

	private onPointerMove = (state: PanningState, event: PointerEvent) => {

		document.body.classList.add(TOUCH_MOVING);

		clearTimeout(this.touchMovingTimeout);

		this.touchMovingTimeout = setTimeout(() => {
			document.body.classList.remove(TOUCH_MOVING);
		}, 1500);


		if (!state.panning) return;

		event.preventDefault();

		//
		// Calculate how far the finger has moved from since it started
		//
		const current = event.pageY;
		const delta = current - state.panning.offset;

		// Update the scroll position. We subtract the delta because
		// dragging down (positive delta) should move the content up (decrease scrollTop).
		state.element.scrollTop = state.panning.startScrollTop - delta;
	};


	private onPointerUp = (state: PanningState, event: PointerEvent) => {
		document.body.classList.remove(TOUCH_ACTIVE);
		state.panning = null;
		//state.element.releasePointerCapture(event.pointerId);
	};

}